import get from 'lodash/get';
import React from 'react';
import snakeCase from 'lodash/snakeCase';
import PropTypes from 'prop-types';

import { Icon, IconName } from '@usealloy/component-library';
import {
  StyledDiv,
  StyledInput,
  StyledLabel,
  StyledLabelOptional,
  StyledWarning,
  StyledWarningWrapper,
  StyledSpan,
} from './InputStyles';

const InputPropTypes = {
  /** The validation error message, if there is one */
  error: PropTypes.string,
  /** Whether or not the input has a validation error */
  hasError: PropTypes.bool,
  /** Label for the input. */
  label: PropTypes.string,
  /** Name for the input. Equivalent to <input name={name} /> */
  name: PropTypes.string,
  /**
   * Validation RegEx for the input.
   * Not used directly; passed to the `register` function prop.
   */
  pattern: PropTypes.instanceOf(RegExp),
  /**
   * Placeholder string for the input
   */
  placeholder: PropTypes.string,
  /**
   * A custom function to style the HTML input element.
   *
   * It should return an object with the html attributes
   * (e.g. `type`, `disabled`) to apply to the `<input>`
   * element.
   *
   * It receives as arguments three of the props to Input:
   * - `label` as its first argument
   * - as its second argument, options/object style:
   *   `{ required, pattern }`.
   *
   * @example
   * const EmailInput = () => {
   *    const [error, setError] = useState('');
   *    const [value, setValue] = useState('');
   *    const hasError = error !== '';
   *    const pattern = /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/;
   *    const changeHandler = (pattern, required) => (event) => {
   *      if (event.target.value === '') {
   *        setError('This field is required.');
   *      } else if (event.target.value.match(pattern) === null) {
   *        setError('Please enter a valid email address.');
   *      }
   *      setValue(event.target.value);
   *    };
   *    const register = (label, { required, pattern }) => ({
   *      type: 'text',
   *      value: value,
   *      onChange: changeHandler(pattern, required),
   *    })
   *
   * }
   */
  register: PropTypes.func.isRequired,
  /** render props */
  render: PropTypes.object,
  /**
   * Whether the input is required.
   * Not used directly; passed to the `register` function prop.
   */
  required: PropTypes.bool,
  /**
   * Whether the input is disabled.
   */
  disabled: PropTypes.bool,
  /** Whether or not to show a <label> for the <input> */
  showLabel: PropTypes.bool,
  /** Type of the text input */
  type: PropTypes.string,
  /** warning message if provided */
  warning: PropTypes.string,
  /** Width for the input. */
  width: PropTypes.number,
};

const InputDefaultProps = {
  error: '',
  hasError: false,
  label: '',
  name: undefined,
  pattern: /[\d\D]+/,
  placeholder: '',
  required: false,
  showLabel: true,
  width: undefined,
};

/**
 * A basic input component.
 * Displays the label and the error message, if given.
 *
 * Extensible behavior via the function-type prop `register`
 *
 * @component
 */
const Input = ({
  error,
  hasError,
  label,
  name,
  pattern,
  placeholder,
  register,
  render,
  required,
  showLabel,
  type,
  warning,
  width,
  disabled,
}) => (
  <StyledDiv>
    {showLabel && label && (
      <StyledLabel htmlFor={label}>
        {label}{' '}
        {!required && <StyledLabelOptional>(Optional)</StyledLabelOptional>}
      </StyledLabel>
    )}
    {warning && (
      <StyledWarningWrapper
        width={width}
      >
        <Icon
          icon={IconName.TriangleWarning}
          title="Warning"
          width={20}
          height={20}
        />
        <StyledWarning>
          {warning}
        </StyledWarning>
      </StyledWarningWrapper>
    )}
    {(get(render, 'input', inputProps => <StyledInput {...inputProps} />)({
      hasError,
      placeholder,
      width,
      id: label,
      ...register(name || snakeCase(label), {
        disabled, required, pattern, type,
      }),
    }))}
    {hasError && <StyledSpan role="alert">{error}</StyledSpan>}
  </StyledDiv>
);

Input.propTypes = InputPropTypes;
Input.defaultProps = InputDefaultProps;

export default Input;
